import { useState, useMemo, useEffect } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import {
  Search,
  Filter,
  Plus,
  Download,
  DollarSign,
  Package,
  Clock,
  CheckCircle,
  Sparkles,
  Building2,
  X,
  Bot,
  FileText,
  ExternalLink,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

import Page from '@/components/page/Page';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatCurrency } from '@/utils/formatters';

import {
  mockPurchaseOrders,
  mockSuppliers,
  mockItems,
  type PurchaseOrder,
} from '../data/mockData';

type StatusFilter = PurchaseOrder['status'] | 'all';

const statusConfig: Record<
  PurchaseOrder['status'],
  {
    label: string;
    className: string;
  }
> = {
  draft: { label: 'Draft', className: 'bg-gray-100 text-gray-700' },
  pending: {
    label: 'Pending Approval',
    className: 'bg-yellow-100 text-yellow-700',
  },
  approved: { label: 'Approved', className: 'bg-blue-100 text-blue-700' },
  sent: { label: 'Sent', className: 'bg-indigo-100 text-indigo-700' },
  confirmed: { label: 'Confirmed', className: 'bg-blue-100 text-blue-700' },
  shipped: { label: 'In Transit', className: 'bg-cyan-100 text-cyan-700' },
  received: { label: 'Delivered', className: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-700' },
};

// Derive priority from order characteristics
function getOrderPriority(po: PurchaseOrder): 'high' | 'medium' | 'low' {
  if (po.totalAmount > 50000) return 'high';
  if (po.totalAmount > 20000) return 'medium';
  return 'low';
}

const priorityConfig = {
  high: { label: 'High', className: 'bg-red-100 text-red-700' },
  medium: { label: 'Medium', className: 'bg-yellow-100 text-yellow-700' },
  low: { label: 'Low', className: 'bg-gray-100 text-gray-700' },
};

// Pre-defined AI-generated order for demo purposes
const AI_GENERATED_ORDER: PurchaseOrder = {
  id: 'po-ai-metalworks',
  poNumber: 'PO-2026-04-001',
  supplierId: 'sup-006', // MetalWorks Inc
  status: 'approved',
  items: [
    {
      itemId: 'item-001', // Silicon Wafers
      quantity: 2500,
      unitPrice: 45,
      totalPrice: 112500,
    },
  ],
  totalAmount: 112500,
  createdAt: new Date().toISOString().split('T')[0],
  expectedDelivery: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0],
  notes:
    'AI-generated order for MetalWorks Inc. Lead time: 14 days. Recommended based on best price and reliability.',
  aiGenerated: true,
  attachments: [
    {
      name: 'PO-2026-04-001.pdf',
      url: '/documents/po-ai-metalworks.pdf',
      type: 'pdf',
    },
  ],
};

export function PurchaseOrdersPage() {
  const { supplierId: supplierIdFilter, showAiOrder } = useSearch({
    strict: false,
  }) as {
    supplierId?: string;
    showAiOrder?: string;
  };
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(
    null,
  );

  // Track if the AI order should be shown (persists in session)
  const [showAiOrderInList, setShowAiOrderInList] = useState(false);

  // Handle showing AI order from query param
  useEffect(() => {
    if (showAiOrder === 'true') {
      // Show the AI order in the list
      setShowAiOrderInList(true);
      // Open the modal for the AI order
      setSelectedOrder(AI_GENERATED_ORDER);
      // Clear the URL param but keep the order visible
      navigate({ to: '.', search: { supplierId: supplierIdFilter } });
    }
  }, [showAiOrder, navigate, supplierIdFilter]);

  const filterSupplier = supplierIdFilter
    ? mockSuppliers.find((s) => s.id === supplierIdFilter)
    : null;

  // Filter and sort purchase orders
  const filteredOrders = useMemo(() => {
    // Start with mock orders, optionally include AI order
    let orders = showAiOrderInList
      ? [AI_GENERATED_ORDER, ...mockPurchaseOrders]
      : [...mockPurchaseOrders];

    // Apply supplier filter if present from URL
    if (supplierIdFilter) {
      orders = orders.filter((po) => po.supplierId === supplierIdFilter);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      orders = orders.filter((po) => {
        const supplier = mockSuppliers.find((s) => s.id === po.supplierId);
        const itemNames = po.items
          .map((item) => mockItems.find((i) => i.id === item.itemId)?.name)
          .join(' ');
        return (
          po.poNumber.toLowerCase().includes(query) ||
          supplier?.name.toLowerCase().includes(query) ||
          itemNames.toLowerCase().includes(query)
        );
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      orders = orders.filter((po) => po.status === statusFilter);
    }

    // Sort: AI-generated orders first, then non-received by expected delivery, then received by delivery date
    const aiOrders = orders.filter((po) => po.id.startsWith('po-ai-'));
    const nonAiOrders = orders.filter((po) => !po.id.startsWith('po-ai-'));

    const nonReceived = nonAiOrders
      .filter((po) => po.status !== 'received')
      .sort(
        (a, b) =>
          new Date(a.expectedDelivery).getTime() -
          new Date(b.expectedDelivery).getTime(),
      );

    const received = nonAiOrders
      .filter((po) => po.status === 'received')
      .sort((a, b) => {
        const dateA = a.deliveredOn ? new Date(a.deliveredOn).getTime() : 0;
        const dateB = b.deliveredOn ? new Date(b.deliveredOn).getTime() : 0;
        return dateB - dateA;
      });

    return [...aiOrders, ...nonReceived, ...received];
  }, [supplierIdFilter, searchQuery, statusFilter, showAiOrderInList]);

  // Stats calculations
  const stats = useMemo(() => {
    const baseOrders = showAiOrderInList
      ? [AI_GENERATED_ORDER, ...mockPurchaseOrders]
      : mockPurchaseOrders;

    const allOrders = supplierIdFilter
      ? baseOrders.filter((po) => po.supplierId === supplierIdFilter)
      : baseOrders;

    return {
      totalOrders: allOrders.length,
      pendingApproval: allOrders.filter((po) => po.status === 'pending').length,
      inTransit: allOrders.filter((po) => po.status === 'shipped').length,
      totalValue: allOrders.reduce((sum, po) => sum + po.totalAmount, 0),
      aiGenerated: allOrders.filter((po) => po.aiGenerated).length,
    };
  }, [supplierIdFilter, showAiOrderInList]);

  // Find high priority pending order for AI insight
  const highPriorityPending = mockPurchaseOrders.find(
    (po) =>
      (po.status === 'pending' || po.status === 'draft') &&
      getOrderPriority(po) === 'high',
  );

  const clearSupplierFilter = () => {
    navigate({ to: '.', search: {} });
  };

  return (
    <Page
      title="Purchase Orders"
      subtitle="Manage and track all purchase orders"
    >
      <div className="flex flex-col gap-6">
        {/* Search and Filters */}
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              type="text"
              placeholder="Search by PO number, supplier, or items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as StatusFilter)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending">Pending Approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="shipped">In Transit</SelectItem>
                <SelectItem value="received">Delivered</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" className="gap-2">
            <Download className="w-4 h-4" />
            Export
          </Button>
          <Button className="gap-2">
            <Plus className="w-4 h-4" />
            Create PO
          </Button>
        </div>

        {/* Active Supplier Filter Banner */}
        {filterSupplier && (
          <div className="flex items-center justify-between px-4 py-3 bg-primary/10 border border-primary/30 rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <Filter className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">
                Filtered by supplier:
              </span>
              <Badge variant="secondary">
                <Building2 className="h-3 w-3 mr-1" />
                {filterSupplier.name}
              </Badge>
              <span className="text-muted-foreground">
                ({filteredOrders.length} order
                {filteredOrders.length !== 1 ? 's' : ''})
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSupplierFilter}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4 mr-1" />
              Clear filter
            </Button>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-muted-foreground">Total Orders</div>
              <Package className="w-5 h-5 text-muted-foreground" />
            </div>
            <div className="text-2xl font-semibold">{stats.totalOrders}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {stats.aiGenerated} AI-generated
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-muted-foreground">
                Pending Approval
              </div>
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="text-2xl font-semibold text-yellow-600">
              {stats.pendingApproval}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Requires action
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-muted-foreground">In Transit</div>
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-2xl font-semibold text-blue-600">
              {stats.inTransit}
            </div>
            <div className="text-xs text-muted-foreground mt-1">On the way</div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-muted-foreground">Total Value</div>
              <DollarSign className="w-5 h-5 text-muted-foreground" />
            </div>
            <div className="text-2xl font-semibold">
              {formatCurrency(stats.totalValue, {
                locale: 'en-US',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </div>
            <div className="text-xs text-muted-foreground mt-1">All orders</div>
          </Card>
        </div>

        {/* AI Insights Banner */}
        {(stats.pendingApproval > 0 || highPriorityPending) && (
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">AI Insights</h3>
              <p className="text-sm text-gray-700">
                You have{' '}
                <span className="font-medium">
                  {stats.pendingApproval} orders
                </span>{' '}
                awaiting approval.
                {highPriorityPending && (
                  <>
                    {' '}
                    <span className="font-medium">
                      {highPriorityPending.poNumber}
                    </span>{' '}
                    is high priority and requires immediate attention.
                  </>
                )}
              </p>
            </div>
            <button
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              onClick={() => setShowReviewModal(true)}
            >
              Review Now
            </button>
          </div>
        )}

        {/* Purchase Orders Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    PO Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Supplier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Items
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Delivery
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredOrders.map((order) => {
                  const supplier = mockSuppliers.find(
                    (s) => s.id === order.supplierId,
                  );
                  const status = statusConfig[order.status];
                  const priority = getOrderPriority(order);
                  const priorityStyle = priorityConfig[priority];
                  const itemNames = order.items
                    .map(
                      (item) =>
                        mockItems.find((i) => i.id === item.itemId)?.name,
                    )
                    .filter(Boolean)
                    .join(', ');
                  const isNewAiOrder = order.id.startsWith('po-ai-');

                  return (
                    <tr
                      key={order.id}
                      className={`hover:bg-muted/30 cursor-pointer transition-colors ${isNewAiOrder ? 'bg-violet-50/50' : ''}`}
                      onClick={() => setSelectedOrder(order)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium font-mono">
                            {order.poNumber}
                          </span>
                          {order.aiGenerated && (
                            <Tooltip>
                              <TooltipTrigger>
                                {isNewAiOrder ? (
                                  <Bot className="w-4 h-4 text-violet-600" />
                                ) : (
                                  <Sparkles className="w-4 h-4 text-primary" />
                                )}
                              </TooltipTrigger>
                              <TooltipContent>
                                {isNewAiOrder
                                  ? 'Just created by AI Procurement Agent'
                                  : 'Generated by AI Procurement Agent'}
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {isNewAiOrder && (
                            <Badge className="bg-violet-100 text-violet-700 border-0 text-[10px] px-1.5 py-0">
                              New
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          By {order.aiGenerated ? 'AI Assistant' : 'Manual'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium">
                          {supplier?.name ?? 'Unknown'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          {order.items.length} item
                          {order.items.length !== 1 ? 's' : ''}
                        </div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="text-xs text-muted-foreground mt-1 max-w-[150px] truncate">
                              {itemNames}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {itemNames}
                          </TooltipContent>
                        </Tooltip>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium">
                          {formatCurrency(order.totalAmount, {
                            locale: 'en-US',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${status.className}`}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          {format(parseISO(order.createdAt), 'MMM d, yyyy')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {order.status === 'received' && order.deliveredOn ? (
                          <div className="flex items-center gap-1 text-sm text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            {format(parseISO(order.deliveredOn), 'MMM d, yyyy')}
                          </div>
                        ) : (
                          <div className="text-sm">
                            {format(
                              parseISO(order.expectedDelivery),
                              'MMM d, yyyy',
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${priorityStyle.className}`}
                        >
                          {priorityStyle.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <Button
                          variant="link"
                          className="p-0 h-auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOrder(order);
                          }}
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredOrders.length === 0 && (
            <div className="p-12 text-center">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">
                No purchase orders found matching your search.
              </p>
            </div>
          )}
        </Card>

        {/* Results Summary */}
        {filteredOrders.length > 0 && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div>
              Showing {filteredOrders.length} of {stats.totalOrders} orders
            </div>
          </div>
        )}
      </div>

      {/* Review Order Modal - Shows only the urgent order AI identified */}
      {showReviewModal &&
        highPriorityPending &&
        (() => {
          const order = highPriorityPending;
          const supplier = mockSuppliers.find((s) => s.id === order.supplierId);
          const itemsSummary = order.items
            .map((item) => {
              const itemData = mockItems.find((i) => i.id === item.itemId);
              return `${itemData?.name} (${item.quantity} units)`;
            })
            .join(', ');

          return (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={() => setShowReviewModal(false)}
            >
              <div
                className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  {/* Modal Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <Sparkles className="w-6 h-6 text-blue-600" />
                      <div>
                        <h2 className="text-2xl font-semibold text-gray-900">
                          Urgent Order Review
                        </h2>
                        <p className="text-sm text-gray-600">
                          This high-priority order requires your immediate
                          attention
                        </p>
                      </div>
                    </div>
                    <button
                      className="text-gray-400 hover:text-gray-600 text-2xl"
                      onClick={() => setShowReviewModal(false)}
                    >
                      ×
                    </button>
                  </div>

                  {/* Order Card */}
                  <div className="border-2 rounded-lg p-5 border-red-200 bg-red-50">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {order.poNumber}
                          </h3>
                          <span className="px-2 py-1 text-xs rounded font-medium bg-red-100 text-red-700">
                            High Priority
                          </span>
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded font-medium">
                            Pending Approval
                          </span>
                        </div>
                        <div className="text-sm text-gray-700 mb-1">
                          <span className="font-medium">Supplier:</span>{' '}
                          {supplier?.name ?? 'Unknown'}
                        </div>
                        <div className="text-sm text-gray-700">
                          <span className="font-medium">Items:</span>{' '}
                          {itemsSummary}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-semibold text-gray-900">
                          {formatCurrency(order.totalAmount, {
                            locale: 'en-US',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Created{' '}
                          {format(parseISO(order.createdAt), 'MMM d, yyyy')}
                        </div>
                      </div>
                    </div>

                    {/* AI Recommendation */}
                    <div className="rounded-lg p-4 mb-4 bg-white">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        AI Recommendation
                      </h4>
                      <p className="text-sm text-gray-700 mb-3">
                        This order meets all procurement policies.{' '}
                        {supplier?.name} offered the best price with acceptable
                        lead time. Approval recommended.
                      </p>
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div>
                          <div className="text-gray-600">
                            Price Competitiveness
                          </div>
                          <div className="font-semibold text-green-600">
                            Excellent
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600">Supplier Rating</div>
                          <div className="font-semibold text-gray-900">
                            {((supplier?.qualityScore ?? 80) / 20).toFixed(1)}
                            /5.0
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600">Delivery Timeline</div>
                          <div className="font-semibold text-green-600">
                            Meets Requirements
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
                        Approve Order
                      </button>
                      <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                        Request Changes
                      </button>
                      <button className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors font-medium">
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Order Details Modal */}
      {selectedOrder && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedOrder(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-2xl font-semibold text-gray-900">
                      {selectedOrder.poNumber}
                    </h2>
                    {selectedOrder.aiGenerated && (
                      <Sparkles className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-3 py-1 rounded text-sm font-medium ${statusConfig[selectedOrder.status].className}`}
                    >
                      {statusConfig[selectedOrder.status].label}
                    </span>
                    <span
                      className={`px-3 py-1 rounded text-sm font-medium ${priorityConfig[getOrderPriority(selectedOrder)].className}`}
                    >
                      {priorityConfig[getOrderPriority(selectedOrder)].label}{' '}
                      Priority
                    </span>
                  </div>
                </div>
                <button
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                  onClick={() => setSelectedOrder(null)}
                >
                  ×
                </button>
              </div>

              {/* Order and Supplier Info */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Order Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Created Date:</span>
                      <span className="font-medium text-gray-900">
                        {format(
                          parseISO(selectedOrder.createdAt),
                          'MMM d, yyyy',
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Created By:</span>
                      <span className="font-medium text-gray-900">
                        {selectedOrder.aiGenerated ? 'AI Assistant' : 'Manual'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Expected Delivery:</span>
                      <span className="font-medium text-gray-900">
                        {format(
                          parseISO(selectedOrder.expectedDelivery),
                          'MMM d, yyyy',
                        )}
                      </span>
                    </div>
                    {selectedOrder.deliveredOn && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Delivered On:</span>
                        <span className="font-medium text-green-600">
                          {format(
                            parseISO(selectedOrder.deliveredOn),
                            'MMM d, yyyy',
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Supplier Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Supplier:</span>
                      <span className="font-medium text-gray-900">
                        {mockSuppliers.find(
                          (s) => s.id === selectedOrder.supplierId,
                        )?.name ?? 'Unknown'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Items:</span>
                      <span className="font-medium text-gray-900">
                        {selectedOrder.items.length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Amount:</span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(selectedOrder.totalAmount, {
                          locale: 'en-US',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">
                  Order Items
                </h3>
                <div className="space-y-2">
                  {selectedOrder.items.map((item, idx) => {
                    const itemData = mockItems.find(
                      (i) => i.id === item.itemId,
                    );
                    return (
                      <div
                        key={idx}
                        className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {itemData?.name ?? 'Unknown Item'}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {item.quantity.toLocaleString()}{' '}
                              {itemData?.unitOfMeasure ?? 'units'} @{' '}
                              {formatCurrency(item.unitPrice, {
                                locale: 'en-US',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </div>
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(item.totalPrice, {
                              locale: 'en-US',
                              currency: 'USD',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Order Status Info */}
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Order Status
                    </h4>
                    <p className="text-sm text-gray-700">
                      {selectedOrder.status === 'received'
                        ? `This order was delivered on ${format(parseISO(selectedOrder.deliveredOn!), 'MMM d, yyyy')}.`
                        : selectedOrder.status === 'shipped'
                          ? `This order is currently in transit and expected to arrive on ${format(parseISO(selectedOrder.expectedDelivery), 'MMM d, yyyy')}.`
                          : selectedOrder.status === 'cancelled'
                            ? 'This order has been cancelled.'
                            : `This order is ${statusConfig[selectedOrder.status].label.toLowerCase()} and expected to be delivered by ${format(parseISO(selectedOrder.expectedDelivery), 'MMM d, yyyy')}.`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedOrder.notes && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
                  <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">
                    {selectedOrder.notes}
                  </p>
                </div>
              )}

              {/* Attachments */}
              {selectedOrder.attachments &&
                selectedOrder.attachments.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      Attachments
                    </h3>
                    <div className="space-y-2">
                      {selectedOrder.attachments.map((attachment, idx) => (
                        <a
                          key={idx}
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                            <FileText className="h-5 w-5 text-red-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {attachment.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              PDF Document
                            </p>
                          </div>
                          <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-gray-700" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                {selectedOrder.status === 'shipped' && (
                  <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
                    Track Shipment
                  </button>
                )}
                {selectedOrder.status === 'pending' && (
                  <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
                    Approve Order
                  </button>
                )}
                {selectedOrder.status === 'draft' && (
                  <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
                    Submit for Approval
                  </button>
                )}
                <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                  Contact Supplier
                </button>
                <button
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  onClick={() => setSelectedOrder(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
